/**
 * @fileoverview Task Queue API endpoints
 * Handles API calls for task management operations
 */

import { pb } from '../helpers/pocketbase_helper';

/**
 * Start a task by updating its start date and status
 * @param {string} taskId - The ID of the task to start
 * @returns {Promise<Object>} Success response with task ID
 * @throws {Error} If task cannot be started
 */
export const startTaskAPI = async (taskId) => {
  try {
    console.log('startTaskAPI called with taskId:', taskId);
    
    if (!taskId) {
      throw new Error('Task ID is required');
    }

    const now = new Date().toISOString();
    console.log('Updating task with inicio:', now);

    // Update the task log with start date
    const result = await pb.collection('log_plan_centro_costo').update(taskId, {
      inicio: now
    });

    console.log('Task updated successfully:', result);

    return {
      success: true,
      taskId,
      startDate: now
    };
  } catch (error) {
    console.error('Error starting task:', error);
    throw new Error(`Failed to start task: ${error.message}`);
  }
};

/**
 * Finish a task by updating its end date and status
 * @param {string} taskId - The ID of the task to finish
 * @returns {Promise<Object>} Success response with task ID
 * @throws {Error} If task cannot be finished
 */
export const finishTaskAPI = async (taskId) => {
  try {
    if (!taskId) {
      throw new Error('Task ID is required');
    }

    const now = new Date().toISOString();

    // Update the task log with end date
    await pb.collection('log_plan_centro_costo').update(taskId, {
      fin: now
    });

    return {
      success: true,
      taskId,
      endDate: now
    };
  } catch (error) {
    console.error('Error finishing task:', error);
    throw new Error(`Failed to finish task: ${error.message}`);
  }
};

/**
 * Update task progress percentage
 * @param {string} tareaOrdenId - The ID of the tarea_orden record
 * @param {number} progress - Progress percentage (0-100)
 * @returns {Promise<Object>} Success response with progress info
 * @throws {Error} If progress cannot be updated
 */
export const updateProgressAPI = async (tareaOrdenId, progress) => {
  try {
    if (!tareaOrdenId) {
      throw new Error('Tarea Orden ID is required');
    }

    if (typeof progress !== 'number' || progress < 0 || progress > 100) {
      throw new Error('Progress must be a number between 0 and 100');
    }

    const now = new Date().toISOString();

    const updateData = {
      porcentaje: Math.round(progress) // Ensure integer
    };

    // If progress reaches 100%, set completion date
    if (progress >= 100) {
      updateData.fin = now;
    }

    await pb.collection('tarea_orden').update(tareaOrdenId, updateData);

    return {
      success: true,
      tareaOrdenId,
      progress: Math.round(progress),
      completed: progress >= 100,
      completionDate: progress >= 100 ? now : null
    };
  } catch (error) {
    console.error('Error updating progress:', error);
    throw new Error(`Failed to update progress: ${error.message}`);
  }
};

/**
 * Get task progress for an order
 * @param {string} ordenId - The ID of the order
 * @returns {Promise<Object|null>} Progress data or null if not found
 * @throws {Error} If progress cannot be retrieved
 */
export const getTaskProgressAPI = async (ordenId) => {
  try {
    if (!ordenId) {
      throw new Error('Order ID is required');
    }


    // Get the task order record
    const result = await pb.collection('tarea_orden').getList(1, 1, {
      filter: `orden_produccion = "${ordenId}"`
    });

    if (result.items.length === 0) {
      return null;
    }

    return result.items[0];
  } catch (error) {
    console.error('Error getting task progress:', error);
    throw new Error(`Failed to get task progress: ${error.message}`);
  }
};

/**
 * Get all tasks for an order with their current status
 * @param {string} ordenId - The ID of the order
 * @returns {Promise<Array>} Array of task logs with status
 * @throws {Error} If tasks cannot be retrieved
 */
export const getOrderTasksAPI = async (ordenId) => {
  try {
    if (!ordenId) {
      throw new Error('Order ID is required');
    }


    // Get all task logs for the order
    const result = await pb.collection('log_plan_centro_costo').getList(1, 50, {
      filter: `orden = "${ordenId}"`,
      sort: 'prioridad'
    });

    return result.items;
  } catch (error) {
    console.error('Error getting order tasks:', error);
    throw new Error(`Failed to get order tasks: ${error.message}`);
  }
};

/**
 * Activate next priority task for an order
 * @param {string} ordenId - The ID of the order
 * @param {number} currentPriority - Current completed task priority
 * @returns {Promise<Object>} Success response with next task info
 * @throws {Error} If next task cannot be activated
 */
export const activateNextTaskAPI = async (ordenId, currentPriority) => {
  try {
    if (!ordenId) {
      throw new Error('Order ID is required');
    }

    if (typeof currentPriority !== 'number') {
      throw new Error('Current priority must be a number');
    }

    const nextPriority = currentPriority + 1;

    // Find the next priority task
    const result = await pb.collection('log_plan_centro_costo').getList(1, 1, {
      filter: `orden = "${ordenId}" && prioridad = ${nextPriority}`,
      sort: 'prioridad'
    });

    if (result.items.length === 0) {
      return {
        success: true,
        nextTask: null,
        message: 'No more tasks to activate'
      };
    }

    const nextTask = result.items[0];

    // Activate the next task
    await pb.collection('log_plan_centro_costo').update(nextTask.id, {
      ready: true
    });

    return {
      success: true,
      nextTask: {
        id: nextTask.id,
        prioridad: nextTask.prioridad,
        ready: true
      }
    };
  } catch (error) {
    console.error('Error activating next task:', error);
    throw new Error(`Failed to activate next task: ${error.message}`);
  }
};
